import { MoldOrder } from '../types/mold';

/**
 * Search function type - takes a MoldOrder and search term, returns boolean
 */
export type SearchFunction = (order: MoldOrder, searchTerm: string) => boolean;

/**
 * Configuration-driven search functions for different mold order fields
 * Each function handles the specific logic for extracting and matching data for that field type
 */
export const moldOrderSearchFunctions: Record<string, SearchFunction> = {
  // Search all relevant fields
  all: (order: MoldOrder, searchTerm: string) => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    return (
      order.id.toString().toLowerCase().includes(lowerSearchTerm) ||
      (order.mold.sbiId?.toLowerCase().includes(lowerSearchTerm) || false) ||
      (order.mold.moldNumber?.toLowerCase().includes(lowerSearchTerm) || false) ||
      (order.mold.moldCode?.toLowerCase().includes(lowerSearchTerm) || false) ||
      (order.mold.moldName?.toLowerCase().includes(lowerSearchTerm) || false) ||
      (order.mold.productParts?.some(p => 
        (p.partNumber?.toLowerCase().includes(lowerSearchTerm) || false) ||
        (p.partName?.toLowerCase().includes(lowerSearchTerm) || false)
      ) || false) ||
      (order.mold.customer?.code?.toLowerCase().includes(lowerSearchTerm) || false) ||
      (order.mold.customer?.name?.toLowerCase().includes(lowerSearchTerm) || false)
    );
  },

  // Search by order ID
  orderId: (order: MoldOrder, searchTerm: string) => {
    return order.id.toString().toLowerCase().includes(searchTerm.toLowerCase());
  },

  // Search by mold ID (sbiId or moldNumber)
  moldId: (order: MoldOrder, searchTerm: string) => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    return (
      (order.mold.sbiId?.toLowerCase().includes(lowerSearchTerm) || false) ||
      (order.mold.moldNumber?.toLowerCase().includes(lowerSearchTerm) || false)
    );
  },

  // Search by customer code or name
  customerCode: (order: MoldOrder, searchTerm: string) => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    return (
      (order.mold.customer?.code?.toLowerCase().includes(lowerSearchTerm) || false) ||
      (order.mold.customer?.name?.toLowerCase().includes(lowerSearchTerm) || false)
    );
  },

  // Search by part number
  partNo: (order: MoldOrder, searchTerm: string) => {
    return order.mold.productParts?.some(p => 
      p.partNumber?.toLowerCase().includes(searchTerm.toLowerCase()) || false
    ) || false;
  },

  // Search by part name
  partName: (order: MoldOrder, searchTerm: string) => {
    return order.mold.productParts?.some(p => 
      p.partName?.toLowerCase().includes(searchTerm.toLowerCase()) || false
    ) || false;
  },

  // Search by mold code (if available)
  moldCode: (order: MoldOrder, searchTerm: string) => {
    return order.mold.moldCode?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
  },

  // Search by mold name (if available)
  moldName: (order: MoldOrder, searchTerm: string) => {
    return order.mold.moldName?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
  },

  // Search by assigned technician
  assignedTo: (order: MoldOrder, searchTerm: string) => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    return (
      (order.assignedTo?.username?.toLowerCase().includes(lowerSearchTerm) || false) ||
      (order.assignedTo?.fullname?.toLowerCase().includes(lowerSearchTerm) || false)
    );
  },

  // Search by mold order type
  type: (order: MoldOrder, searchTerm: string) => {
    return order.type?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
  },

  // Search by status
  status: (order: MoldOrder, searchTerm: string) => {
    return order.status?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
  },

  // Example: Search by priority (easy to add new fields)
  priority: (order: MoldOrder, searchTerm: string) => {
    return order.priority?.toString().includes(searchTerm) || false;
  },

  // Example: Search by machine number (if available in order details)
  machineNumber: (order: MoldOrder, searchTerm: string) => {
    const machineNumber = order.orderDetail?.form?.machineNumber;
    return machineNumber ? machineNumber.toString().toLowerCase().includes(searchTerm.toLowerCase()) : false;
  }
};

/**
 * Performs search on a mold order using the configuration-driven approach
 * @param order - The mold order to search in
 * @param searchTerm - The term to search for
 * @param searchFieldId - The ID of the search field (corresponds to company config)
 * @returns boolean indicating if the order matches the search
 */
export const searchMoldOrder = (
  order: MoldOrder, 
  searchTerm: string, 
  searchFieldId: string
): boolean => {
  // Get the search function for the specified field
  const searchFunction = moldOrderSearchFunctions[searchFieldId];
  
  // If no search function is found, fall back to 'all' search
  if (!searchFunction) {
    console.warn(`Search function not found for field '${searchFieldId}', falling back to 'all'`);
    return moldOrderSearchFunctions.all(order, searchTerm);
  }

  return searchFunction(order, searchTerm);
};

/**
 * Filters an array of mold orders based on search criteria
 * @param moldOrders - Array of mold orders to filter
 * @param searchTerm - The term to search for
 * @param searchFieldId - The ID of the search field
 * @returns Filtered array of mold orders
 */
export const filterMoldOrdersBySearch = (
  moldOrders: MoldOrder[],
  searchTerm: string,
  searchFieldId: string
): MoldOrder[] => {
  if (!searchTerm.trim()) {
    return moldOrders;
  }

  return moldOrders.filter(order => 
    searchMoldOrder(order, searchTerm, searchFieldId)
  );
};